import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { marketplaceApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ShoppingBag,
  ArrowLeft,
  Plus,
  Search,
  DollarSign,
  Phone,
  User,
  Tag,
  Image,
  Loader2,
  Heart,
  MessageCircle,
  ChevronRight,
  X,
} from 'lucide-react';

const categories = [
  { id: 'all', label: 'All Items' },
  { id: 'furniture', label: 'Furniture' },
  { id: 'appliances', label: 'Appliances' },
  { id: 'electronics', label: 'Electronics' },
  { id: 'sports', label: 'Sports & Fitness' },
  { id: 'kids', label: 'Kids & Baby' },
  { id: 'other', label: 'Other' },
];

export default function Marketplace() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [selectedListing, setSelectedListing] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newListing, setNewListing] = useState({
    title: '',
    description: '',
    price: '',
    category: 'other',
  });

  const hoaId = user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchListings();
  }, [activeCategory]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const category = activeCategory === 'all' ? null : activeCategory;
      const data = await marketplaceApi.list(hoaId, category).catch(() => getDemoListings());
      setListings(data);
    } catch (error) {
      setListings(getDemoListings());
    } finally {
      setLoading(false);
    }
  };

  const getDemoListings = () => [
    { listing_id: 'list_001', title: 'IKEA Kallax Shelf Unit', description: 'White 4x4 cube shelf unit in excellent condition. Perfect for books or storage.', price: 1500, currency: 'ZAR', category: 'furniture', seller_name: 'Sarah M.', photos: [], status: 'active', created_at: new Date().toISOString() },
    { listing_id: 'list_002', title: 'Sony 55" 4K Smart TV', description: '2023 model, barely used. Selling due to upgrade. Remote included.', price: 8500, currency: 'ZAR', category: 'electronics', seller_name: 'Mike D.', photos: [], status: 'active', created_at: new Date(Date.now() - 86400000).toISOString() },
    { listing_id: 'list_003', title: 'Peloton Bike', description: 'Gen 3 Peloton bike with mat and weights. Great condition.', price: 15000, currency: 'ZAR', category: 'sports', seller_name: 'Jennifer L.', photos: [], status: 'active', created_at: new Date(Date.now() - 172800000).toISOString() },
    { listing_id: 'list_004', title: 'Baby Crib with Mattress', description: 'Convertible crib, grows with your child. Mattress included.', price: 2800, currency: 'ZAR', category: 'kids', seller_name: 'David K.', photos: [], status: 'active', created_at: new Date(Date.now() - 259200000).toISOString() },
    { listing_id: 'list_005', title: 'Dyson V11 Vacuum', description: 'Cordless vacuum cleaner. All attachments included.', price: 5500, currency: 'ZAR', category: 'appliances', seller_name: 'Anna P.', photos: [], status: 'active', created_at: new Date(Date.now() - 345600000).toISOString() },
  ];

  const handleCreate = async () => {
    if (!newListing.title || !newListing.price) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await marketplaceApi.create({
        hoa_id: hoaId,
        title: newListing.title,
        description: newListing.description,
        price: parseFloat(newListing.price),
        category: newListing.category,
        photos: [],
      });

      toast.success('Listing created!');
      setView('list');
      setNewListing({ title: '', description: '', price: '', category: 'other' });
      fetchListings();
    } catch (error) {
      toast.error('Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContact = (listing) => {
    toast.success(`Contact ${listing.seller_name} via community message`);
  };

  const formatPrice = (price, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) return 'Today';
    if (diff < 172800000) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredListings = searchQuery 
    ? listings.filter(l => l.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : listings;

  if (view === 'detail' && selectedListing) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Item Details</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6 space-y-4">
          {/* Image Placeholder */}
          <div className="aspect-square bg-slate-200 rounded-2xl flex items-center justify-center">
            <Image className="w-16 h-16 text-slate-400" />
          </div>

          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <Badge className="mb-2">{selectedListing.category}</Badge>
                <h2 className="text-xl font-bold font-manrope text-slate-900">{selectedListing.title}</h2>
                <p className="text-2xl font-bold text-sky-600 mt-1">{formatPrice(selectedListing.price, selectedListing.currency)}</p>
              </div>
              <Button variant="ghost" size="icon">
                <Heart className="w-5 h-5 text-slate-400" />
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-500">
              <User className="w-4 h-4" />
              <span>{selectedListing.seller_name}</span>
              <span>•</span>
              <span>{formatDate(selectedListing.created_at)}</span>
            </div>

            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 mb-2">Description</h3>
                <p className="text-slate-600">{selectedListing.description}</p>
              </CardContent>
            </Card>

            <Button 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
              onClick={() => handleContact(selectedListing)}
              data-testid="contact-seller-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Contact Seller
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Create Listing</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6 space-y-6">
          {/* Photo Upload */}
          <Button 
            variant="outline" 
            className="w-full h-32 border-dashed flex flex-col gap-2"
            data-testid="add-photos-btn"
          >
            <Image className="w-8 h-8 text-slate-400" />
            <span className="text-sm text-slate-500">Add Photos</span>
          </Button>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Title *</label>
              <Input
                placeholder="What are you selling?"
                value={newListing.title}
                onChange={(e) => setNewListing({ ...newListing, title: e.target.value })}
                className="h-12"
                data-testid="title-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Price *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="number"
                  placeholder="0.00"
                  value={newListing.price}
                  onChange={(e) => setNewListing({ ...newListing, price: e.target.value })}
                  className="pl-10 h-12"
                  data-testid="price-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Category</label>
              <Select 
                value={newListing.category} 
                onValueChange={(value) => setNewListing({ ...newListing, category: value })}
              >
                <SelectTrigger className="h-12" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.id !== 'all').map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                placeholder="Describe your item..."
                value={newListing.description}
                onChange={(e) => setNewListing({ ...newListing, description: e.target.value })}
                className="min-h-24"
                data-testid="description-input"
              />
            </div>
          </div>

          <Button 
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
            onClick={handleCreate}
            disabled={submitting}
            data-testid="create-listing-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Tag className="w-5 h-5 mr-2" />
                Post Listing
              </>
            )}
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Marketplace</h1>
          </div>
          <Button 
            onClick={() => setView('create')}
            className="rounded-full bg-slate-900 hover:bg-slate-800"
            data-testid="sell-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Sell
          </Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-full"
            data-testid="search-input"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full whitespace-nowrap ${activeCategory === cat.id ? 'bg-slate-900' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`category-${cat.id}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        ) : filteredListings.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredListings.map((listing, index) => (
              <Card 
                key={listing.listing_id}
                className="overflow-hidden animate-slideUp cursor-pointer hover:shadow-md transition-shadow"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => { setSelectedListing(listing); setView('detail'); }}
                data-testid={`listing-${listing.listing_id}`}
              >
                <div className="aspect-square bg-slate-100 flex items-center justify-center">
                  <ShoppingBag className="w-8 h-8 text-slate-300" />
                </div>
                <CardContent className="p-3">
                  <p className="font-medium text-slate-900 text-sm line-clamp-1">{listing.title}</p>
                  <p className="font-bold text-sky-600 mt-1">{formatPrice(listing.price, listing.currency)}</p>
                  <p className="text-xs text-slate-400 mt-1">{listing.seller_name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">No items found</h3>
              <p className="text-slate-500 text-sm mb-4">Be the first to list something!</p>
              <Button onClick={() => setView('create')} className="rounded-full bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-1" />
                Sell Item
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
